if (!customElements.get('custom-review-popup')) {
  customElements.define(
    'custom-review-popup',
    class CustomReviewPopup extends HTMLElement {
      constructor() {
        super();
        this.cookieName = 'shopify:custom-review-popup';
        this.discountCode = this.dataset.discountCode;
        this.repeatOpen = this.dataset.repeatOpen;
        this.inactivitySeconds = parseInt(this.dataset.inactivitySeconds) || 20;
      }

      connectedCallback() {
        this._originalParent = document.body;

        if (window.MutationObserver) {
          this._positionObserver = new MutationObserver(() => {
            if (this.parentElement !== this._originalParent && this.parentElement !== null) {
              console.warn('[CustomReviewPopup] was moved unexpectedly. Restoring to body.');
              document.body.appendChild(this);
            }
          });

          this._positionObserver.observe(document.body, {
            childList: true,
            subtree: false,
            attributes: false
          });
        }

        this.closeButton = this.querySelector('.custom-review-popup__close');
        this.overlay = this.querySelector('.drawer__overlay');
        this.ctaButton = this.querySelector('.custom-review-popup__cta-button');

        const modalContent = this.querySelector('.modal-content');
        if (modalContent) {
          modalContent.addEventListener('click', (event) => {
            if (event.target.closest('.custom-review-popup__close')) {
              event.preventDefault();
              event.stopPropagation();
              this.hide();
            }
          });
        }

        if (this.overlay) {
          this.overlay.addEventListener('click', this.hide.bind(this));
        }

        if (this.ctaButton) {
          this.ctaButton.addEventListener('click', this.applyDiscount.bind(this));
        }

        const noThanksButton = this.querySelector('.custom-review-popup__no-thanks');
        if (noThanksButton) {
          noThanksButton.addEventListener('click', this.hide.bind(this));
        }

        document.addEventListener('keyup', (event) => {
          if (event.code === 'Escape' && this.isVisible) {
            this.hide();
          }
        });

        this.handleOpen();
      }

      async handleOpen() {
        if (Shopify.designMode) return;

        let savedData = this.getStorage();
        if (savedData && savedData.opened) {
          return false;
        }

        if (this.dataset.hideIfDiscount === 'true') {
          const hasOrderLevelDiscount = await this.hasCartDiscount();
          if (hasOrderLevelDiscount) return false;
        }

        this._triggerFired = false;
        this.setupInactivityTrigger();
      }

      /**
       * Fire after a period of inactivity (no interaction).
       * Used on both desktop and mobile.
       */
      setupInactivityTrigger() {
        const activityEvents = ['touchstart', 'scroll', 'click', 'mousemove', 'keydown'];

        const resetTimer = () => {
          clearTimeout(this._inactivityTimer);
          if (this._triggerFired) return;
          this._inactivityTimer = setTimeout(() => {
            if (this._triggerFired) return;
            this._triggerFired = true;
            cleanup();
            this.show();
          }, this.inactivitySeconds * 1000);
        };

        const cleanup = () => {
          activityEvents.forEach((evt) => {
            window.removeEventListener(evt, resetTimer, { passive: true });
          });
        };

        this._cleanupInactivity = () => {
          clearTimeout(this._inactivityTimer);
          cleanup();
        };

        activityEvents.forEach((evt) => {
          window.addEventListener(evt, resetTimer, { passive: true });
        });

        // Start the first timer immediately
        resetTimer();
      }

      show() {
        if (this.isVisible) return;

        this.updateDynamicHeading();

        if (this.hasAttribute('hidden')) {
          this.removeAttribute('hidden');
        }

        this.hidden = false;
        this.classList.add('active');
        this.setAttribute('open', '');

        this.offsetHeight; // Force reflow

        setTimeout(() => {
          this.setAttribute('active', '');
        }, 75);

        if (window.FoxTheme && FoxTheme.a11y && FoxTheme.a11y.trapFocus) {
          FoxTheme.a11y.trapFocus(this, this.ctaButton || this.querySelector('button'));
        }

        this.isVisible = true;
        this.setRepeatOpen();
      }

      updateDynamicHeading() {
        if (this.dataset.staticHeading === 'true') return;

        const FREQ_MAP = {
          '30': { pct: 25, packs: 1 },
          '60': { pct: 36, packs: 2 },
          '90': { pct: 40, packs: 3 }
        };

        let freq = null;
        try {
          freq = window.personalised?.bundle?.getFrequency();
        } catch (_) {}

        if (!freq) {
          try {
            const state = JSON.parse(localStorage.getItem('personalised_bundle_state'));
            freq = state?.frequency;
          } catch (_) {}
        }

        const data = FREQ_MAP[freq] || FREQ_MAP['30'];
        const packText = data.packs === 1 ? 'pack' : data.packs + ' packs';

        const el = this.querySelector('.custom-review-popup__heading');
        if (el) el.innerHTML = 'Checkout today and get <strong>' + data.pct + '% OFF</strong> your first ' + packText;
      }

      hide() {
        this.classList.remove('active');
        this.removeAttribute('open');
        this.removeAttribute('active');

        if (window.FoxTheme && FoxTheme.a11y && FoxTheme.a11y.removeTrapFocus) {
          FoxTheme.a11y.removeTrapFocus();
        }

        this.isVisible = false;
        this.hidden = true;
      }

      applyDiscount(event) {
        event.preventDefault();

        const discountCode = this.discountCode;
        if (!discountCode) return;

        if (typeof gtag === 'function') {
          gtag('event', 'review_discount_popup_claimed', {
            discount_code: discountCode
          });
        }

        this.hide();

        // Apply discount and redirect to checkout
        window.location.href = `/discount/${encodeURIComponent(discountCode)}?redirect=/checkout`;
      }

      setRepeatOpen() {
        if (Shopify.designMode) return;

        let expires;
        switch (this.repeatOpen) {
          case 'no_repeat':
            expires = 365;
            break;
          case 'every_day':
            expires = 1;
            break;
          case 'every_3_days':
            expires = 3;
            break;
          case 'every_week':
            expires = 7;
            break;
          default:
            expires = 1;
            break;
        }

        this.setStorage({ opened: true }, expires);
      }

      getStorage() {
        const item = localStorage.getItem(this.cookieName);
        if (!item) return null;

        try {
          const data = JSON.parse(item);
          const expiresAt = new Date(data.expiresAt);
          if (expiresAt < new Date()) {
            localStorage.removeItem(this.cookieName);
            return null;
          }
          return data.value;
        } catch (e) {
          return null;
        }
      }

      setStorage(value, days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);

        const data = {
          value: value,
          expiresAt: date.toISOString()
        };

        localStorage.setItem(this.cookieName, JSON.stringify(data));
      }

      async hasCartDiscount() {
        try {
          if (this._cartCheckOnce) return this._cartCheckOnce;
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 3000);
          const p = (async () => {
            const res = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
            clearTimeout(timeoutId);
            if (!res.ok) return false;
            const cart = await res.json();
            if (cart.discount_codes && cart.discount_codes.length > 0) return true;
            return Array.isArray(cart.cart_level_discount_applications) && cart.cart_level_discount_applications.length > 0;
          })().catch(() => false);
          this._cartCheckOnce = p;
          return await p;
        } catch (_) {
          return false;
        }
      }

      disconnectedCallback() {
        if (this._positionObserver) {
          this._positionObserver.disconnect();
        }
        if (this._cleanupInactivity) {
          this._cleanupInactivity();
        }
      }
    }
  );
}
